/**
 * SDTestApp Hub HTTP client — drives the local /api/hub/v1 REST API.
 *
 * Replaces the iframe + postMessage bridge for the erp-migration Configuration page.
 * Appsmith continues to use {@link SdTestAppBridge} in sdTestAppClient.ts.
 */

import {
  devicesForSingleDeviceConfig,
  extractMcuId,
  isSdTestDummyDevice,
  listSdTestDevices,
  readDeviceField,
  readDeviceLabelSerial,
  SDTEST_FIELD_KEYS,
  SDTESTAPP_DEFAULT_PORT,
  type ConfigureResult,
  type SdTestConnectionStatus,
  type SdTestDemographics,
  type SdTestDevice,
  type SdTestStatus,
} from "./sdTestAppClient";

export type { ConfigureResult, SdTestConnectionStatus, SdTestDemographics, SdTestDevice, SdTestStatus };
export {
  devicesForSingleDeviceConfig,
  extractMcuId,
  isSdTestDummyDevice,
  listSdTestDevices,
  readDeviceField,
  readDeviceLabelSerial,
  SDTEST_FIELD_KEYS,
  SDTESTAPP_DEFAULT_PORT,
};

export function buildSdTestAppLocalUrl(port: string = SDTESTAPP_DEFAULT_PORT): string {
  const normalizedPort = String(port || SDTESTAPP_DEFAULT_PORT).trim() || SDTESTAPP_DEFAULT_PORT;
  return `https://127.0.0.1:${normalizedPort}`;
}

/**
 * Browser fetch base URL for the local SDTestApp engine.
 * In Vite dev, route through same-origin `/__sdtestapp/<port>` so duplicate/mismatched
 * localhost CAs do not block health and production API calls.
 */
export function buildSdTestAppFetchBaseUrl(port: string = SDTESTAPP_DEFAULT_PORT): string {
  const normalizedPort = String(port || SDTESTAPP_DEFAULT_PORT).trim() || SDTESTAPP_DEFAULT_PORT;
  const isViteDev = Boolean(import.meta.env && import.meta.env.DEV);
  if (isViteDev && typeof window !== "undefined" && window.location?.origin) {
    return `${window.location.origin}/__sdtestapp/${normalizedPort}`;
  }
  return buildSdTestAppLocalUrl(normalizedPort);
}

export interface SdTestHttpClientListeners {
  onStatusChange?: (status: SdTestStatus, connectionStatus: SdTestConnectionStatus) => void;
  onDemographics?: (demographics: SdTestDemographics | null) => void;
}

const STATUS_OFFLINE: SdTestStatus = { text: "SDTestApp Offline", color: "#DC3545" };
const STATUS_CONNECTED: SdTestStatus = { text: "SDTestApp Connected", color: "#28A745" };

function normalizeFetchError(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
}

/** Browser-level fetch failures (no HTTP response from SDTestApp on 127.0.0.1). */
export function describeSdTestAppFetchFailure(error: unknown): string {
  const raw = normalizeFetchError(error);
  const lowered = raw.toLowerCase();
  if (
    raw === "Failed to fetch" ||
    lowered.includes("networkerror") ||
    lowered.includes("load failed")
  ) {
    return (
      "SDTestApp on this bench did not respond (127.0.0.1). " +
      "The engine may be busy with a long modem command, restarting, or blocked by HTTPS trust in the browser. " +
      "Check the SDTestApp tray, wait for configure or CELL 3 to finish, then retry."
    );
  }
  return raw;
}

export class SdTestAppHubError extends Error {
  readonly statusCode: number;
  readonly code?: string;

  constructor(message: string, statusCode: number, code?: string) {
    super(message);
    this.name = "SdTestAppHubError";
    this.statusCode = statusCode;
    this.code = code;
  }
}

async function readJsonObject(response: Response): Promise<Record<string, unknown>> {
  const text = await response.text();
  const trimmed = text.trimStart();
  if (trimmed.startsWith("<")) {
    throw new Error(
      "SDTestApp returned HTML instead of JSON. Confirm SDTestApp is running on this bench.",
    );
  }
  try {
    const parsed = JSON.parse(text) as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      throw new Error("SDTestApp response was not a JSON object.");
    }
    return parsed as Record<string, unknown>;
  } catch (error) {
    if (error instanceof Error && error.message === "SDTestApp response was not a JSON object.") {
      throw error;
    }
    const preview = text.length > 160 ? `${text.slice(0, 160)}…` : text;
    throw new Error(`SDTestApp returned non-JSON (HTTP ${response.status}): ${preview}`);
  }
}

export interface SdTestConnectorRefreshResult {
  success: boolean;
  connectorId: string;
  demographics: SdTestDemographics;
  warning?: string;
  demographicsUnavailable?: boolean;
}

export class SdTestConnectorRefreshError extends Error {
  readonly statusCode: number;
  readonly payload?: Record<string, unknown>;

  constructor(message: string, statusCode: number, payload?: Record<string, unknown>) {
    super(message);
    this.name = "SdTestConnectorRefreshError";
    this.statusCode = statusCode;
    this.payload = payload;
  }
}

const CONNECTOR_REFRESH_TIMEOUT_MS = 60_000;

export class SdTestAppHttpClient {
  private port: string;
  private listeners: SdTestHttpClientListeners;
  private connectionStatus: SdTestConnectionStatus = "connecting";
  private status: SdTestStatus = STATUS_OFFLINE;
  private lastDemographics: SdTestDemographics | null = null;
  private pollTimer: ReturnType<typeof setInterval> | null = null;

  constructor(port: string = SDTESTAPP_DEFAULT_PORT, listeners: SdTestHttpClientListeners = {}) {
    this.port = String(port || SDTESTAPP_DEFAULT_PORT).trim();
    this.listeners = listeners;
  }

  get baseUrl(): string {
    return buildSdTestAppFetchBaseUrl(this.port);
  }

  getStatus(): SdTestStatus {
    return this.status;
  }

  getConnectionStatus(): SdTestConnectionStatus {
    return this.connectionStatus;
  }

  /** Prefer non-dummy for single-device config; raw demographics still include dummy. */
  getActiveDevices(): SdTestDevice[] {
    return devicesForSingleDeviceConfig(listSdTestDevices(this.lastDemographics));
  }

  startPolling(intervalMilliseconds = 3000): void {
    this.stopPolling();
    void this.refresh();
    this.pollTimer = setInterval(() => {
      void this.refresh();
    }, intervalMilliseconds);
  }

  stopPolling(): void {
    if (this.pollTimer !== null) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  dispose(): void {
    this.stopPolling();
  }

  async refresh(): Promise<void> {
    try {
      const healthResponse = await fetch(`${this.baseUrl}/api/hub/v1/health`, {
        method: "GET",
      });
      if (!healthResponse.ok) {
        this.markDisconnected();
        return;
      }
      const devicesResponse = await fetch(`${this.baseUrl}/api/hub/v1/devices`, {
        method: "GET",
      });
      if (!devicesResponse.ok) {
        this.markDisconnected();
        return;
      }
      const demographics = (await devicesResponse.json()) as SdTestDemographics;
      this.connectionStatus = "connected";
      this.lastDemographics = demographics;
      this.listeners.onDemographics?.(demographics);
      this.updateStatus(demographics);
    } catch {
      this.markDisconnected();
    }
  }

  async refreshConnector(
    connectorId: string,
    timeoutMilliseconds = CONNECTOR_REFRESH_TIMEOUT_MS,
  ): Promise<SdTestConnectorRefreshResult> {
    const trimmedConnectorId = String(connectorId ?? "").trim();
    if (trimmedConnectorId === "") {
      throw new SdTestConnectorRefreshError("Connector id is required.", 400);
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMilliseconds);
    let response: Response;
    try {
      try {
        response = await fetch(`${this.baseUrl}/api/hub/v1/devices/refresh`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ connectorId: trimmedConnectorId }),
          signal: controller.signal,
        });
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") {
          throw new SdTestConnectorRefreshError(
            "Device refresh timed out. The device may still be bootstrapping — try again or reconnect the cable.",
            504,
          );
        }
        throw new SdTestConnectorRefreshError(normalizeFetchError(error), 0);
      }

      const payload = (await response.json()) as Record<string, unknown>;

      if (response.status === 404) {
        throw new SdTestConnectorRefreshError(
          String(payload.error ?? "Connector not found — check cable and SDTestApp scanner."),
          404,
          payload,
        );
      }
      if (response.status === 422) {
        throw new SdTestConnectorRefreshError(String(payload.error ?? "Device refresh failed."), 422, payload);
      }
      if (response.status === 400) {
        throw new SdTestConnectorRefreshError(
          String(payload.error ?? "Invalid device refresh request."),
          400,
          payload,
        );
      }
      if (response.status === 504) {
        throw new SdTestConnectorRefreshError(
          String(payload.error ?? "Device refresh timed out."),
          504,
          payload,
        );
      }

      if (response.status === 503 || (response.ok && payload.demographicsUnavailable === true)) {
        const warning = String(
          payload.warning ?? "Demographics snapshot unavailable after refresh.",
        );
        await this.refresh();
        const demographics = this.lastDemographics ?? { devices: [], deviceCount: 0 };
        return {
          success: true,
          connectorId: String(payload.connectorId ?? trimmedConnectorId),
          demographics,
          warning,
          demographicsUnavailable: true,
        };
      }

      if (!response.ok) {
        throw new SdTestConnectorRefreshError(
          String(payload.error ?? `Device refresh failed (${response.status}).`),
          response.status,
          payload,
        );
      }

      const demographics = this.demographicsFromRefreshPayload(payload);
      this.applyDemographics(demographics);
      return {
        success: true,
        connectorId: String(payload.connectorId ?? trimmedConnectorId),
        demographics,
      };
    } finally {
      clearTimeout(timeoutId);
    }
  }

  async wirelessCellCheckIn(connectorId: string, timeoutMilliseconds = 120000): Promise<void> {
    const trimmedConnectorId = String(connectorId ?? "").trim();
    if (trimmedConnectorId === "") {
      throw new Error("Connector id is required for wireless check-in.");
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMilliseconds);
    const timeoutSeconds = Math.max(5, Math.round(timeoutMilliseconds / 1000));
    let response: Response;
    try {
      try {
        const checkInUrl = `${this.baseUrl}/api/hub/v1/devices/${encodeURIComponent(trimmedConnectorId)}/wireless-cell-check-in`;
        response = await fetch(checkInUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ timeoutSeconds }),
          signal: controller.signal,
        });
        if (response.status === 404) {
          response = await fetch(
            `${this.baseUrl}/api/hub/v1/devices/${encodeURIComponent(trimmedConnectorId)}/commands`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                commandKey: "cell_prop.set",
                wait: true,
                timeoutSeconds,
                args: { setting: 3 },
              }),
              signal: controller.signal,
            },
          );
        }
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") {
          throw new SdTestAppHubError(
            `Wireless portal check-in timed out after ${timeoutSeconds} seconds (CELL 3 / modem registration).`,
            504,
            "wireless_portal_check_in_timeout",
          );
        }
        throw new SdTestAppHubError(describeSdTestAppFetchFailure(error), 0, "sdtestapp_unreachable");
      }

      const payload = await readJsonObject(response);
      if (!response.ok || payload.success !== true) {
        const code = typeof payload.code === "string" ? payload.code : undefined;
        const errorText = String(
          payload.error ?? payload.message ?? `Wireless check-in failed (${response.status})`,
        );
        throw new SdTestAppHubError(errorText, response.status, code ?? "wireless_portal_check_in_failed");
      }
    } finally {
      clearTimeout(timeoutId);
    }
  }

  async configureAndWait(
    connectorId: string,
    modelNumber: string,
    timeoutMilliseconds = 60000,
    wirelessPortalCheckIn = false,
  ): Promise<ConfigureResult> {
    const effectiveTimeoutMilliseconds = wirelessPortalCheckIn
      ? Math.max(timeoutMilliseconds, 180000)
      : timeoutMilliseconds;
    const timeoutSeconds = Math.max(5, Math.round(effectiveTimeoutMilliseconds / 1000));
    let response: Response;
    try {
      response = await fetch(`${this.baseUrl}/api/hub/v1/configure`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          connectorId,
          modelNumber,
          timeoutSeconds,
          wirelessPortalCheckIn,
        }),
      });
    } catch (error) {
      throw new SdTestAppHubError(describeSdTestAppFetchFailure(error), 0, "sdtestapp_unreachable");
    }

    const payload = (await response.json()) as Record<string, unknown>;
    if (!response.ok) {
      const code = typeof payload.code === "string" ? payload.code : undefined;
      let errorText = String(payload.error ?? `Configuration failed (${response.status})`);
      if (code === "wireless_portal_check_in_failed") {
        errorText = `Wireless portal check-in failed after SKU configure (CELL 3): ${errorText}`;
      }
      throw new SdTestAppHubError(errorText, response.status, code);
    }

    const firmware = String(payload.firmware ?? "");
    if (!firmware) {
      throw new Error("Configuration succeeded but firmware was missing from the response");
    }

    const demographics = this.demographicsFromRefreshPayload(payload);
    this.applyDemographics(demographics);

    const configureResponse =
      payload.configure && typeof payload.configure === "object"
        ? (payload.configure as Record<string, unknown>)
        : { action: "configure", successCount: 1 };

    return {
      success: true,
      firmware,
      response: configureResponse,
    };
  }

  private demographicsFromRefreshPayload(payload: Record<string, unknown>): SdTestDemographics {
    const devices = Array.isArray(payload.devices) ? (payload.devices as SdTestDevice[]) : [];
    const deviceCount =
      typeof payload.deviceCount === "number" ? payload.deviceCount : devices.length;
    return {
      devices,
      deviceCount,
      status: typeof payload.status === "string" ? payload.status : undefined,
    };
  }

  private applyDemographics(demographics: SdTestDemographics): void {
    this.connectionStatus = "connected";
    this.lastDemographics = demographics;
    this.listeners.onDemographics?.(demographics);
    this.updateStatus(demographics);
  }

  private markDisconnected(): void {
    this.connectionStatus = "disconnected";
    this.lastDemographics = null;
    this.listeners.onDemographics?.(null);
    this.setStatus(STATUS_OFFLINE);
  }

  private setStatus(status: SdTestStatus): void {
    this.status = status;
    this.listeners.onStatusChange?.(status, this.connectionStatus);
  }

  private updateStatus(_demographics: SdTestDemographics | null): void {
    if (this.connectionStatus === "disconnected") {
      this.setStatus(STATUS_OFFLINE);
      return;
    }
    const activeCount = this.getActiveDevices().length;
    if (activeCount === 1) {
      this.setStatus(STATUS_CONNECTED);
    } else if (activeCount === 0) {
      this.setStatus({ text: "SDTestApp Connected (No Active Devices)", color: "#FFC107" });
    } else {
      this.setStatus({ text: `SDTestApp Too Many Devices (${activeCount})`, color: "#DC3545" });
    }
  }
}
